import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BellRing, Send, Clock, Repeat, Users, XCircle, CheckCircle2, Smartphone } from "lucide-react";
import { toast } from "sonner";

// Deep-link targets — value is the Expo route the app opens on when the push is tapped.
const TAB_TARGETS = [
  { value: "/", label: "Train (Home)" },
  { value: "/elite-jump", label: "Elite Jump" },
  { value: "/book", label: "Book" },
  { value: "/equipment", label: "Equipment" },
  { value: "/more", label: "More" },
];

const MODES = [
  { value: "now", label: "Send now", icon: Send },
  { value: "once", label: "Schedule once", icon: Clock },
  { value: "recurring", label: "Recurring", icon: Repeat },
];

export default function AdminPush() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("/");
  const [mode, setMode] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [sending, setSending] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [deviceCount, setDeviceCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/push/campaigns");
      setCampaigns(data.campaigns || []);
      setDeviceCount(data.device_count || 0);
    } catch { toast.error("Failed to load campaigns"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setTitle(""); setBody(""); setTarget("/"); setScheduledAt(""); };

  const submit = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and message are required"); return; }
    if (mode !== "now" && !scheduledAt) { toast.error("Pick a date & time"); return; }
    setSending(true);
    try {
      if (mode === "now") {
        const { data } = await api.post("/admin/push/send-now", {
          title: title.trim(), body: body.trim(), action_url: target || null,
        });
        toast.success(`Sent to ${data.sent_count} device${data.sent_count === 1 ? "" : "s"}.`);
      } else {
        await api.post("/admin/push/schedule", {
          title: title.trim(), body: body.trim(), action_url: target || null,
          schedule_type: mode === "recurring" ? "recurring" : "once",
          scheduled_at: new Date(scheduledAt).toISOString(),
          frequency: mode === "recurring" ? frequency : null,
        });
        toast.success(mode === "recurring" ? "Recurring push scheduled." : "Push scheduled.");
      }
      reset();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/admin/push/campaigns/${id}/cancel`);
      toast.success("Campaign cancelled.");
      await load();
    } catch { toast.error("Cancel failed"); }
  };

  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const labelFor = (url) => TAB_TARGETS.find((t) => t.value === url)?.label || "Train (Home)";

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-push-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box App</p>
        <h1 className="font-display text-3xl text-white uppercase tracking-tight">Push Notifications</h1>
        <p className="text-sm text-zinc-400 mt-1">Broadcast to every Ronin Box device. Send instantly, schedule a one-off, or set a recurring cadence. Tapping the push opens the tab you pick.</p>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Composer */}
        <div className="lg:col-span-3 border border-[#27272A] bg-[#0F0F0F]">
          <div className="border-b border-[#27272A] px-6 py-4 flex items-center gap-2">
            <BellRing className="h-4 w-4 text-[#FF331F]" />
            <div className="font-display uppercase tracking-widest text-sm">Compose</div>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={65}
                placeholder="Fresh 3-round session just dropped" className="mt-1 bg-black border-[#27272A] text-white" data-testid="push-title-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Message</label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={180} rows={3}
                placeholder="Lace up — Coach Chris added a new audio workout. Tap to train." className="mt-1 bg-black border-[#27272A] text-white" data-testid="push-body-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Open this tab on tap</label>
              <select value={target} onChange={(e) => setTarget(e.target.value)}
                className="mt-1 w-full bg-black border border-[#27272A] text-white text-sm px-3 py-2 rounded-sm" data-testid="push-target-select">
                {TAB_TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Delivery</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {MODES.map(({ value, label, icon: Icon }) => (
                  <button key={value} onClick={() => setMode(value)} data-testid={`push-mode-${value}`}
                    className={`flex flex-col items-center gap-1 px-2 py-3 rounded-sm border text-[10px] uppercase tracking-widest font-bold ${mode === value ? "border-[#FF331F] bg-[#FF331F]/10 text-white" : "border-[#27272A] text-zinc-400 hover:text-white"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {mode !== "now" && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{mode === "recurring" ? "First run at" : "Send at"}</label>
                  <Input type="datetime-local" value={scheduledAt} min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)} onChange={(e) => setScheduledAt(e.target.value)}
                    className="mt-1 bg-black border-[#27272A] text-white" data-testid="push-datetime-input" />
                </div>
                {mode === "recurring" && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Repeat</label>
                    <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
                      className="mt-1 w-full bg-black border border-[#27272A] text-white text-sm px-3 py-2 rounded-sm" data-testid="push-frequency-select">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            <Button onClick={submit} disabled={sending} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs" data-testid="push-submit-btn">
              <Send className={`h-4 w-4 mr-2 ${sending ? "animate-pulse" : ""}`} />
              {sending ? "Working…" : mode === "now" ? `Send to ${deviceCount} device${deviceCount === 1 ? "" : "s"}` : "Schedule push"}
            </Button>
          </div>
        </div>

        {/* Side: device count */}
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-[#27272A] bg-[#0F0F0F] p-5">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="h-4 w-4 text-emerald-400" />
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Registered devices</div>
            </div>
            <div className="font-display text-4xl text-white uppercase tracking-tight" data-testid="push-device-count">{deviceCount}</div>
            <p className="text-xs text-zinc-500 mt-2">Devices that granted notification permission. Actual delivery requires a published Android/iOS build.</p>
          </div>
        </div>
      </div>

      {/* Campaign history */}
      <div className="border border-[#27272A] bg-[#0F0F0F]">
        <div className="border-b border-[#27272A] px-6 py-4 flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#FF331F]" />
          <div className="font-display uppercase tracking-widest text-sm">Campaigns</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
        ) : campaigns.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No campaigns yet. Compose your first push above.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]">
              <tr>
                <th className="p-3 text-left">Title</th>
                <th className="p-3 text-left">Opens</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Next / Last run</th>
                <th className="p-3 text-left">Sent</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-b border-[#27272A]" data-testid={`push-campaign-${c.id}`}>
                  <td className="p-3 text-zinc-200">
                    <div className="font-semibold">{c.title}</div>
                    <div className="text-xs text-zinc-500 line-clamp-1">{c.body}</div>
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">{labelFor(c.action_url)}</td>
                  <td className="p-3 text-zinc-300 text-xs uppercase tracking-widest">
                    {c.schedule_type === "recurring" ? `Recurring · ${c.frequency}` : c.schedule_type === "once" ? "Scheduled" : "Instant"}
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">
                    {c.status === "scheduled" ? `Next: ${fmt(c.next_run_at)}` : `Last: ${fmt(c.last_run_at)}`}
                  </td>
                  <td className="p-3 text-zinc-300"><span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{c.sent_count}</span></td>
                  <td className="p-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="p-3 text-right">
                    {c.status === "scheduled" && (
                      <button onClick={() => cancel(c.id)} data-testid={`push-cancel-${c.id}`}
                        className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-[10px] uppercase tracking-widest font-bold">
                        <XCircle className="h-3.5 w-3.5" /> Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { cls: "text-emerald-400", icon: CheckCircle2, label: "Sent" },
    scheduled: { cls: "text-amber-400", icon: Clock, label: "Scheduled" },
    cancelled: { cls: "text-zinc-500", icon: XCircle, label: "Cancelled" },
  };
  const s = map[status] || map.sent;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${s.cls}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}
